<script>
  // No authentication required for About page
  import { onMount } from 'svelte';
  let showTeamModal = false;
</script>

<!-- Hero Section with background image, no overlay, white text -->
<section class="relative flex items-center justify-center min-h-[480px] md:min-h-[600px] w-full hero-gradient" style="background: url('/hero_3.jpg') center/cover no-repeat;">
  <div class="absolute inset-0 bg-[#334155]/40"></div>
  <div class="relative z-10 w-full py-16 md:py-24 text-center">
    <h1 class="text-5xl md:text-6xl font-bold text-white mb-6 mt-5">
      Real-Time CGM Platform
      <span class="block text-white/80">for Clinical Research</span>
    </h1>
    <p class="text-xl text-white mb-8 max-w-3xl mx-auto">
      Transform your clinical trials with real-time continuous glucose monitoring data capture and advanced analytics. 
      SUMMA Portal delivers instant insights from Dexcom G7 and Abbott Libre devices, enabling faster decision-making and improved study outcomes.
    </p>
    <div class="flex flex-col sm:flex-row gap-4 justify-center px-4 sm:px-0">
      <a href="mailto:info@precisiondigitalhealth.com" class="cta-button border-2 border-white text-white hover:bg-white hover:text-slate-600 px-8 py-3 rounded-lg font-semibold text-lg text-center">
        Contact Us
      </a>
      <button 
        on:click={() => showTeamModal = true}
        class="cta-button bg-slate-600 hover:bg-slate-700 text-white px-8 py-3 rounded-lg font-semibold text-lg transition-all duration-200"
      >
        Meet Our Team
      </button>
    </div>
  </div>
</section>

<!-- Features Section -->
<section id="features" class="py-20 bg-white w-full flex-shrink-0 px-2 sm:px-4">
  <div>
    <div class="text-center mb-16">
      <h2 class="text-4xl font-bold text-gray-900 mb-4">Real-Time CGM Solutions</h2>
      <p class="text-xl text-gray-600 max-w-2xl mx-auto">
        Advanced continuous glucose monitoring with instant data capture and analytics for clinical research
      </p>
    </div>
    
    <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
      <!-- Real-Time CGM Data -->
      <div class="feature-card p-8 flex flex-col items-center text-center">
        <div class="relative w-full max-w-xs mb-4">
          <img src="/Participant%20Dashboard.jpg" alt="Participant Dashboard" class="rounded-sm shadow-lg w-full object-cover" />
          <div class="absolute inset-0 flex items-center justify-center">
            <svg class="w-32 h-32 text-white opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
            </svg>
          </div>
        </div>
        <h3 class="text-xl font-semibold text-gray-900 mb-4">Real-Time CGM Data</h3>
        <p class="text-gray-600">
          Instant continuous glucose monitoring data capture from Dexcom G7 and Abbott Libre devices with live streaming capabilities.
        </p>
      </div>
      
      <!-- Live CGM Compliance -->
      <div class="feature-card p-8 flex flex-col items-center text-center">
        <div class="relative w-full max-w-xs mb-4">
          <img src="/Participant%20CGM%20Wear%20Compliance.jpg" alt="CGM Wear Compliance" class="rounded-sm shadow-lg w-full object-cover" />
          <div class="absolute inset-0 flex items-center justify-center">
            <svg class="w-32 h-32 text-white opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
            </svg>
          </div>
        </div>
        <h3 class="text-xl font-semibold text-gray-900 mb-4">Live Wear Compliance</h3>
        <p class="text-gray-600">
          Real-time glucose wear compliance with instant AGP reports, time-in-range calculations, and live trend analysis.
        </p>
      </div>
      
      <!-- Instant AGP Reports -->
      <div class="feature-card p-8 flex flex-col items-center text-center">
        <div class="relative w-full max-w-xs mb-4">
          <img src="/Participant%20Real%20Time%20AGP%20Report.jpg" alt="Real Time AGP Report" class="rounded-sm shadow-lg w-full object-cover" />
          <div class="absolute inset-0 flex items-center justify-center">
            <svg class="w-32 h-32 text-white opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
          </div>
        </div>
        <h3 class="text-xl font-semibold text-gray-900 mb-4">Instant AGP Reports</h3>
        <p class="text-gray-600">
          Live Ambulatory Glucose Profile reports with real-time glucose variability analysis and trend visualization.
        </p>
      </div>
      
      <!-- Study Management -->
      <div class="feature-card p-8 flex flex-col items-center text-center">
        <div class="relative w-full max-w-xs mb-4">
          <img src="/Site%20Participants%20List.jpg" alt="Site Participants List" class="rounded-sm shadow-lg w-full object-cover" />
          <div class="absolute inset-0 flex items-center justify-center">
            <svg class="w-32 h-32 text-white opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
            </svg>
          </div>
        </div>
        <h3 class="text-xl font-semibold text-gray-900 mb-4">Study Management</h3>
        <p class="text-gray-600">
          Complete study lifecycle management from setup to completion with flexible configuration options.
        </p>
      </div>
      
      <!-- Safety Monitoring -->
      <div class="feature-card p-8 flex flex-col items-center text-center">
        <div class="relative w-full max-w-xs mb-4">
          <img src="/Participant%20Real%20Time%20CGM%20Measurements.jpg" alt="Real Time CGM Measurements" class="mb-4 rounded-sm shadow-lg w-full object-cover" />
          <div class="absolute inset-0 flex items-center justify-center">
            <svg class="w-32 h-32 text-white opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
            </svg>
          </div>
        </div>
        <h3 class="text-xl font-semibold text-gray-900 mb-4">Safety Monitoring</h3>
        <p class="text-gray-600">
          Proactive safety monitoring with automated alerts and real-time subject safety assessments.
        </p>
      </div>
      
      <!-- Regulatory Compliance -->
      <div class="feature-card p-8 flex flex-col items-center text-center">
        <div class="relative w-full max-w-xs mb-4">
          <img src="/Participant%20CGM%20Baseline%20Compare%20Analysis.jpg" alt="CGM Baseline Compare Analysis" class="rounded-sm shadow-lg w-full object-cover" />
          <div class="absolute inset-0 flex items-center justify-center">
            <svg class="w-32 h-32 text-white opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"></path>
            </svg>
          </div>
        </div>
        <h3 class="text-xl font-semibold text-gray-900 mb-4">Regulatory Ready</h3>
        <p class="text-gray-600">
          Built-in regulatory compliance features ensuring your studies meet FDA and international standards.
        </p>
      </div>
    </div>
  </div>
</section>

<!-- Platform Section -->
<section id="platform" class="py-20 bg-gray-50 w-full flex-shrink-0 px-2 sm:px-4">
  <div class="max-w-7xl mx-auto">
    <div class="text-center mb-16">
      <h2 class="text-4xl font-bold text-gray-900 mb-4">Complete CGM Platform</h2>
      <p class="text-xl text-gray-600 max-w-3xl mx-auto">
        SUMMA Portal specializes in real-time continuous glucose monitoring with instant data capture, live analytics, and comprehensive clinical research capabilities
      </p>
    </div>
            <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <!-- Authentication & User Management -->
          <div class="bg-white rounded-lg shadow-md p-6">
            <div class="flex items-center mb-4">
              <div class="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                </svg>
              </div>
              <h3 class="text-lg font-semibold text-gray-900">Authentication & Security</h3>
            </div>
            <p class="text-gray-600 text-sm mb-3">Secure Auth0 integration with role-based access control, multi-organization support, and complete audit trails.</p>
            <ul class="text-xs text-gray-500 space-y-1">
              <li>• OAuth2/OpenID Connect authentication</li>
              <li>• Granular permissions & role management</li>
              <li>• Multi-tenant organization support</li>
            </ul>
          </div>

          <!-- Study Management -->
          <div class="bg-white rounded-lg shadow-md p-6">
            <div class="flex items-center mb-4">
              <div class="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
              </div>
              <h3 class="text-lg font-semibold text-gray-900">Study Management</h3>
            </div>
            <p class="text-gray-600 text-sm mb-3">Complete study lifecycle management with customizable properties, feature flags, and comprehensive analytics.</p>
            <ul class="text-xs text-gray-500 space-y-1">
              <li>• Study creation & configuration</li>
              <li>• Protocol & phase management</li>
              <li>• Custom properties & feature flags</li>
            </ul>
          </div>

          <!-- Real-Time CGM Integration -->
          <div class="bg-white rounded-lg shadow-md p-6">
            <div class="flex items-center mb-4">
              <div class="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                <svg class="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                </svg>
              </div>
              <h3 class="text-lg font-semibold text-gray-900">Real-Time CGM Integration</h3>
            </div>
            <p class="text-gray-600 text-sm mb-3">Live streaming CGM data from Dexcom G7 and Abbott Libre View 3 with instant data capture and processing.</p>
            <ul class="text-xs text-gray-500 space-y-1">
              <li>• Live Dexcom G7 streaming</li>
              <li>• Real-time Abbott Libre data</li>
              <li>• Instant data processing</li>
            </ul>
          </div>

          <!-- Live Glucose Analytics -->
          <div class="bg-white rounded-lg shadow-md p-6">
            <div class="flex items-center mb-4">
              <div class="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mr-3">
                <svg class="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                </svg>
              </div>
              <h3 class="text-lg font-semibold text-gray-900">Live Glucose Analytics</h3>
            </div>
            <p class="text-gray-600 text-sm mb-3">Real-time glucose analytics with instant AGP reports, live time-in-range calculations, and continuous trend monitoring.</p>
            <ul class="text-xs text-gray-500 space-y-1">
              <li>• Live AGP report generation</li>
              <li>• Real-time time-in-range analysis</li>
              <li>• Instant glucose trend detection</li>
            </ul>
          </div>

          <!-- Compliance Monitoring -->
          <div class="bg-white rounded-lg shadow-md p-6">
            <div class="flex items-center mb-4">
              <div class="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center mr-3">
                <svg class="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <h3 class="text-lg font-semibold text-gray-900">Compliance Tracking</h3>
            </div>
            <p class="text-gray-600 text-sm mb-3">Automated compliance monitoring with detailed reporting, alerts, and adherence analytics across devices and studies.</p>
            <ul class="text-xs text-gray-500 space-y-1">
              <li>• Device & study compliance</li>
              <li>• Automated alerts & notifications</li>
              <li>• Compliance analytics & reporting</li>
            </ul>
          </div>

          <!-- Communication -->
          <div class="bg-white rounded-lg shadow-md p-6">
            <div class="flex items-center mb-4">
              <div class="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mr-3">
                <svg class="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
                </svg>
              </div>
              <h3 class="text-lg font-semibold text-gray-900">Communication</h3>
            </div>
            <p class="text-gray-600 text-sm mb-3">Multi-level messaging system with participant, site, and study communications, plus automated messaging workflows.</p>
            <ul class="text-xs text-gray-500 space-y-1">
              <li>• Multi-level messaging system</li>
              <li>• Automated message delivery</li>
              <li>• Multi-language support</li>
            </ul>
          </div>

          <!-- Real-Time Analytics -->
          <div class="bg-white rounded-lg shadow-md p-6">
            <div class="flex items-center mb-4">
              <div class="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center mr-3">
                <svg class="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                </svg>
              </div>
              <h3 class="text-lg font-semibold text-gray-900">Real-Time Analytics</h3>
            </div>
            <p class="text-gray-600 text-sm mb-3">Live analytics dashboards with instant CGM data visualization, real-time reporting, and continuous monitoring capabilities.</p>
            <ul class="text-xs text-gray-500 space-y-1">
              <li>• Live CGM dashboards</li>
              <li>• Real-time data visualization</li>
              <li>• Instant trend analysis</li>
            </ul>
          </div>

          <!-- Forms & Data Collection -->
          <div class="bg-white rounded-lg shadow-md p-6">
            <div class="flex items-center mb-4">
              <div class="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mr-3">
                <svg class="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
              </div>
              <h3 class="text-lg font-semibold text-gray-900">Forms & Data Collection</h3>
            </div>
            <p class="text-gray-600 text-sm mb-3">Electronic data capture with custom forms, real-time validation, and comprehensive data collection workflows.</p>
            <ul class="text-xs text-gray-500 space-y-1">
              <li>• Custom form builder</li>
              <li>• Real-time data validation</li>
              <li>• Electronic data capture</li>
            </ul>
          </div>

          <!-- Scheduling -->
          <div class="bg-white rounded-lg shadow-md p-6">
            <div class="flex items-center mb-4">
              <div class="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center mr-3">
                <svg class="w-6 h-6 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                </svg>
              </div>
              <h3 class="text-lg font-semibold text-gray-900">Study Scheduling</h3>
            </div>
            <p class="text-gray-600 text-sm mb-3">Flexible study scheduling with phase management, activity planning, and automated schedule distribution.</p>
            <ul class="text-xs text-gray-500 space-y-1">
              <li>• Phase-based scheduling</li>
              <li>• Activity management</li>
              <li>• Automated schedule distribution</li>
            </ul>
          </div>

          <!-- Safety Monitoring -->
          <div class="bg-white rounded-lg shadow-md p-6">
            <div class="flex items-center mb-4">
              <div class="w-10 h-10 bg-rose-100 rounded-lg flex items-center justify-center mr-3">
                <svg class="w-6 h-6 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                </svg>
              </div>
              <h3 class="text-lg font-semibold text-gray-900">Safety Monitoring</h3>
            </div>
            <p class="text-gray-600 text-sm mb-3">Proactive safety monitoring with real-time alerts, automated safety assessments, and comprehensive safety reporting.</p>
            <ul class="text-xs text-gray-500 space-y-1">
              <li>• Real-time safety alerts</li>
              <li>• Automated safety assessments</li>
              <li>• Comprehensive safety reporting</li>
            </ul>
          </div>

          <!-- Regulatory Compliance -->
          <div class="bg-white rounded-lg shadow-md p-6">
            <div class="flex items-center mb-4">
              <div class="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center mr-3">
                <svg class="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"></path>
                </svg>
              </div>
              <h3 class="text-lg font-semibold text-gray-900">Regulatory Compliance</h3>
            </div>
            <p class="text-gray-600 text-sm mb-3">Built-in regulatory compliance with FDA standards, GDPR compliance, complete audit trails, and data privacy protection.</p>
            <ul class="text-xs text-gray-500 space-y-1">
              <li>• FDA & international standards</li>
              <li>• Complete audit trails</li>
              <li>• GDPR & privacy compliance</li>
            </ul>
          </div>

          <!-- Internationalization -->
          <div class="bg-white rounded-lg shadow-md p-6">
            <div class="flex items-center mb-4">
              <div class="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center mr-3">
                <svg class="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"></path>
                </svg>
              </div>
              <h3 class="text-lg font-semibold text-gray-900">Global Support</h3>
            </div>
            <p class="text-gray-600 text-sm mb-3">Multi-language support with internationalization, timezone management, and regional compliance for global studies.</p>
            <ul class="text-xs text-gray-500 space-y-1">
              <li>• Multi-language support</li>
              <li>• Global timezone management</li>
              <li>• Regional compliance support</li>
            </ul>
          </div>

          <!-- AWS Cloud Infrastructure -->
          <div class="bg-white rounded-lg shadow-md p-6">
            <div class="flex items-center mb-4">
              <div class="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center mr-3">
                <svg class="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z"></path>
                </svg>
              </div>
              <h3 class="text-lg font-semibold text-gray-900">AWS Cloud Infrastructure</h3>
            </div>
            <p class="text-gray-600 text-sm mb-3">Fully hosted on AWS with multi-region deployment, auto-scaling, high availability, and comprehensive security features.</p>
            <ul class="text-xs text-gray-500 space-y-1">
              <li>• Multi-region deployment</li>
              <li>• Auto-scaling & high availability</li>
              <li>• AWS security & compliance</li>
            </ul>
          </div>

          <!-- Complementary Devices -->
          <div class="bg-white rounded-lg shadow-md p-6">
            <div class="flex items-center mb-4">
              <div class="w-10 h-10 bg-lime-100 rounded-lg flex items-center justify-center mr-3">
                <svg class="w-6 h-6 text-lime-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"></path>
                </svg>
              </div>
              <h3 class="text-lg font-semibold text-gray-900">Complementary Devices</h3>
            </div>
            <p class="text-gray-600 text-sm mb-3">Comprehensive device ecosystem including SMBG, weight scales, blood pressure monitors, activity trackers, and SpO2 devices.</p>
            <ul class="text-xs text-gray-500 space-y-1">
              <li>• SMBG, weight, BP monitors</li>
              <li>• Activity trackers & SpO2</li>
              <li>• Unified data platform</li>
            </ul>
          </div>

          <!-- Complete API Integration -->
          <div class="bg-white rounded-lg shadow-md p-6">
            <div class="flex items-center mb-4">
              <div class="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center mr-3">
                <svg class="w-6 h-6 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"></path>
                </svg>
              </div>
              <h3 class="text-lg font-semibold text-gray-900">Complete API Integration</h3>
            </div>
            <p class="text-gray-600 text-sm mb-3">Full platform integration capabilities with RESTful APIs, webhooks, SDKs, and custom integration solutions.</p>
            <ul class="text-xs text-gray-500 space-y-1">
              <li>• RESTful APIs & webhooks</li>
              <li>• SDKs & custom integrations</li>
              <li>• Real-time data sync</li>
            </ul>
          </div>
        </div>
  </div>
</section>

<!-- About Section -->
<section id="about" class="py-20 w-full flex-shrink-0 relative" style="background: url('/hero_1.jpg') center/cover no-repeat;">
  <div class="absolute inset-0 bg-[#334155]/50"></div>
  <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="grid lg:grid-cols-2 gap-12 items-center">
      <div>
        <h2 class="text-4xl font-bold text-white mb-6">About Precision Digital Health</h2>
        <p class="text-xl text-gray-200 mb-6">
          We are the leading experts in real-time continuous glucose monitoring for clinical research, specializing in instant CGM data capture and live analytics.
        </p>
        <p class="text-lg text-gray-300 mb-8">
          Sponsors and CROs trust PDH to deliver real-time CGM insights that accelerate clinical trials, enabling faster decision-making and improved patient outcomes through our advanced glucose monitoring platform.
        </p>
        <div class="flex flex-col sm:flex-row gap-4">
          <a href="mailto:info@precisiondigitalhealth.com" class="cta-button border-2 border-white text-white hover:bg-white hover:text-slate-600 px-8 py-3 rounded-lg font-semibold text-lg text-center">
            Contact Us
          </a>
        </div>
      </div>
      <div class="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
        <h3 class="text-2xl font-semibold text-white mb-6">Why Choose SUMMA Portal?</h3>
                    <div class="space-y-4">
              <div class="flex items-start">
                <div class="w-6 h-6 bg-white rounded-full flex items-center justify-center mr-4 mt-1">
                  <svg class="w-4 h-4 text-slate-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                  </svg>
                </div>
                <div>
                  <h4 class="text-white font-semibold">Real-Time CGM Expertise</h4>
                  <p class="text-gray-300">Specialized in live continuous glucose monitoring with instant data capture and analytics</p>
                </div>
              </div>
              <div class="flex items-start">
                <div class="w-6 h-6 bg-white rounded-full flex items-center justify-center mr-4 mt-1">
                  <svg class="w-4 h-4 text-slate-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                  </svg>
                </div>
                <div>
                  <h4 class="text-white font-semibold">Instant Analytics</h4>
                  <p class="text-gray-300">Live AGP reports and real-time glucose trend analysis for faster clinical insights</p>
                </div>
              </div>
              <div class="flex items-start">
                <div class="w-6 h-6 bg-white rounded-full flex items-center justify-center mr-4 mt-1">
                  <svg class="w-4 h-4 text-slate-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                  </svg>
                </div>
                <div>
                  <h4 class="text-white font-semibold">Regulatory Compliant</h4>
                  <p class="text-gray-300">Fully validated real-time CGM platform with complete regulatory compliance</p>
                </div>
              </div>
            </div>
      </div>
    </div>
  </div>
</section>

<!-- Our Partners Section -->
<section id="partners" class="py-20 bg-white w-full flex-shrink-0">
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="text-center mb-16">
      <h2 class="text-4xl font-bold text-gray-900 mb-4">Our Partners</h2>
      <p class="text-xl text-gray-600 max-w-2xl mx-auto">
        We are proud to collaborate with leading companies in healthcare and technology.
      </p>
    </div>
            <div class="flex flex-wrap justify-center items-start gap-12">
          <div class="flex flex-col items-center w-64 text-center">
            <a href="https://www.abbott.com" target="_blank" rel="noopener" class="block mb-4">
              <img src="/Abbott_Laboratories_logo.svg" alt="Abbott Logo" class="h-16 md:h-20 w-auto object-contain grayscale hover:grayscale-0 transition duration-200" />
            </a>
            <p class="text-gray-700 italic text-base">"SUMMA Portal has transformed our clinical data workflows and enabled new levels of compliance."<br><span class="font-semibold">— Abbott</span></p>
          </div>
          <div class="flex flex-col items-center w-64 text-center">
            <a href="https://www.dexcom.com" target="_blank" rel="noopener" class="block mb-4">
              <img src="/Dexcom_logo.svg" alt="Dexcom Logo" class="h-16 md:h-20 w-auto object-contain grayscale hover:grayscale-0 transition duration-200" />
            </a>
            <p class="text-gray-700 italic text-base">"The integration with SUMMA Portal is seamless and supports our mission for real-time diabetes care."<br><span class="font-semibold">— Dexcom</span></p>
          </div>
          <div class="flex flex-col items-center w-64 text-center">
            <a href="https://www.novonordisk.com" target="_blank" rel="noopener" class="block mb-4">
              <img src="/Novo-Nordisk-Logo.svg" alt="Novo Nordisk Logo" class="h-16 md:h-20 w-auto object-contain grayscale hover:grayscale-0 transition duration-200" />
            </a>
            <p class="text-gray-700 italic text-base">"Partnering with Precision Digital Health has accelerated our research and improved patient outcomes."<br><span class="font-semibold">— Novo Nordisk</span></p>
          </div>
        </div>
  </div>
  </section>

<!-- Team Modal -->
{#if showTeamModal}
  <div class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
    <!-- Background overlay -->
    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" on:click={() => showTeamModal = false}></div>

      <!-- Modal panel -->
      <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
        <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <!-- Header -->
          <div class="flex items-center justify-between mb-6">
            <h2 class="text-3xl font-bold text-gray-900">Meet the PDH Team</h2>
            <button 
              on:click={() => showTeamModal = false}
              class="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          <!-- Introduction -->
          <div class="mb-8">
            <p class="text-lg text-gray-700 leading-relaxed">
              At Precision Digital Health, we are passionate about transforming the clinical trial space. Founded by clinical research experts, our mission is to empower healthcare and life sciences organizations with real-time, actionable patient data—bridging the gap between clinical trials and real-world care.
            </p>
            <p class="text-lg text-gray-700 leading-relaxed mt-4">
              Precision Digital Health leads the way in real-time patient data for therapeutic differentiation and value-based care. As the industry shifts from science-centric development to patient-centricity, the need for timely, meaningful insights has never been greater. By investing in robust infrastructure and leveraging real-time data, we help our partners reduce time to market and improve patient outcomes.
            </p>
          </div>

          <!-- Executive Team -->
          <div class="mb-12">
            <h3 class="text-2xl font-bold text-gray-900 mb-8">Executive Team</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <!-- Thomas Wells -->
              <div class="text-center">
                <div class="relative mb-4 group w-32 h-32 mx-auto">
                  <a href="https://www.linkedin.com/in/pdhanalytics/" target="_blank" rel="noopener noreferrer" class="block">
                    <img 
                      src="/team/pdh_thomas_wells.jpg" 
                      alt="Thomas Wells" 
                      class="w-32 h-32 rounded-full object-cover border-2 border-slate-600 grayscale group-hover:grayscale-0 transition-all duration-300"
                    />
                    <div class="absolute inset-0 rounded-full bg-blue-600 bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                      <svg class="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </div>
                  </a>
                </div>
                <h4 class="text-lg font-semibold text-gray-900">Thomas Wells, MS</h4>
                <p class="text-sm text-gray-600 font-medium">CEO & Co-Founder</p>
              </div>

              <!-- Steve Bilawey -->
              <div class="text-center">
                <div class="relative mb-4 group w-32 h-32 mx-auto">
                  <a href="https://www.linkedin.com/in/steve-bilawey-7454/" target="_blank" rel="noopener noreferrer" class="block">
                    <img 
                      src="/team/steve_bilawey_2.jpg" 
                      alt="Steve Bilawey" 
                      class="w-32 h-32 rounded-full object-cover border-2 border-slate-600 grayscale group-hover:grayscale-0 transition-all duration-300"
                    />
                    <div class="absolute inset-0 rounded-full bg-blue-600 bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                      <svg class="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </div>
                  </a>
                </div>
                <h4 class="text-lg font-semibold text-gray-900">Steve Bilawey, MBA</h4>
                <p class="text-sm text-gray-600 font-medium">CBO & Co-Founder</p>
              </div>

              <!-- Michael Bui -->
              <div class="text-center">
                <div class="relative mb-4 group w-32 h-32 mx-auto">
                  <a href="https://www.linkedin.com/company/precision-digital-health-inc/about/" target="_blank" rel="noopener noreferrer" class="block">
                    <img 
                      src="/team/pdh_michael_bui.jpg" 
                      alt="Michael Bui" 
                      class="w-32 h-32 rounded-full object-cover border-2 border-slate-600 grayscale group-hover:grayscale-0 transition-all duration-300"
                    />
                    <div class="absolute inset-0 rounded-full bg-blue-600 bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                      <svg class="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </div>
                  </a>
                </div>
                <h4 class="text-lg font-semibold text-gray-900">Michael Bui</h4>
                <p class="text-sm text-gray-600 font-medium">CTO & Co-Founder</p>
              </div>

              <!-- Boris Gundelach -->
              <div class="text-center">
                <div class="relative mb-4 group w-32 h-32 mx-auto">
                  <a href="https://www.linkedin.com/in/couchworx/" target="_blank" rel="noopener noreferrer" class="block">
                    <img 
                      src="/team/Boris_Portrait.jpg" 
                      alt="Boris Gundelach" 
                      class="w-32 h-32 rounded-full object-cover border-2 border-slate-600 grayscale group-hover:grayscale-0 transition-all duration-300"
                    />
                    <div class="absolute inset-0 rounded-full bg-blue-600 bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                      <svg class="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </div>
                  </a>
                </div>
                <h4 class="text-lg font-semibold text-gray-900">Boris Gundelach</h4>
                <p class="text-sm text-gray-600 font-medium">Director, Product and Application Strategy</p>
              </div>

              <!-- Mark Rice -->
              <div class="text-center">
                <div class="relative mb-4 group w-32 h-32 mx-auto">
                  <a href="https://www.linkedin.com/in/markrice1/" target="_blank" rel="noopener noreferrer" class="block">
                    <img 
                      src="/team/Mark-I-1-scaled.jpg" 
                      alt="Mark Rice" 
                      class="w-32 h-32 rounded-full object-cover border-2 border-slate-600 grayscale group-hover:grayscale-0 transition-all duration-300"
                    />
                    <div class="absolute inset-0 rounded-full bg-blue-600 bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                      <svg class="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </div>
                  </a>
                </div>
                <h4 class="text-lg font-semibold text-gray-900">Mark Rice</h4>
                <p class="text-sm text-gray-600 font-medium">Vice President, Business Development & Sales</p>
              </div>

              <!-- Maria Limosnero -->
              <div class="text-center">
                <div class="relative mb-4 group w-32 h-32 mx-auto">
                  <a href="https://www.linkedin.com/company/precision-digital-health-inc/about/" target="_blank" rel="noopener noreferrer" class="block">
                    <img 
                      src="/team/Maria_Limosnero.jpg" 
                      alt="Maria Limosnero" 
                      class="w-32 h-32 rounded-full object-cover border-2 border-slate-600 grayscale group-hover:grayscale-0 transition-all duration-300"
                    />
                    <div class="absolute inset-0 rounded-full bg-blue-600 bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                      <svg class="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </div>
                  </a>
                </div>
                <h4 class="text-lg font-semibold text-gray-900">Maria Limosnero</h4>
                <p class="text-sm text-gray-600 font-medium">Director, Compliance & Training</p>
              </div>

              <!-- Marco Grabow -->
              <div class="text-center">
                <div class="relative mb-4 group w-32 h-32 mx-auto">
                  <a href="https://www.linkedin.com/in/marco-g-626a34164/" target="_blank" rel="noopener noreferrer" class="block">
                    <img 
                      src="/team/Marco_Grabow_ProfilePic.jpg" 
                      alt="Marco Grabow" 
                      class="w-32 h-32 rounded-full object-cover border-2 border-slate-600 grayscale group-hover:grayscale-0 transition-all duration-300"
                    />
                    <div class="absolute inset-0 rounded-full bg-blue-600 bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                      <svg class="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </div>
                  </a>
                </div>
                <h4 class="text-lg font-semibold text-gray-900">Marco Grabow</h4>
                <p class="text-sm text-gray-600 font-medium">Manager-IT, Information Security & Data Protection Officer</p>
              </div>

              <!-- Mohan Ramalingam -->
              <div class="text-center">
                <div class="relative mb-4 group w-32 h-32 mx-auto">
                  <a href="https://www.linkedin.com/company/precision-digital-health-inc/about/" target="_blank" rel="noopener noreferrer" class="block">
                    <img 
                      src="/team/Mohanakrishnan_Ramalingam.jpg" 
                      alt="Mohan Ramalingam" 
                      class="w-32 h-32 rounded-full object-cover border-2 border-slate-600 grayscale group-hover:grayscale-0 transition-all duration-300"
                    />
                    <div class="absolute inset-0 rounded-full bg-blue-600 bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                      <svg class="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </div>
                  </a>
                </div>
                <h4 class="text-lg font-semibold text-gray-900">Mohan Ramalingam</h4>
                <p class="text-sm text-gray-600 font-medium">Vice President, Data Services & Client Delivery</p>
              </div>

              <!-- Haritha Yellasiri -->
              <div class="text-center">
                <div class="relative mb-4 group w-32 h-32 mx-auto">
                  <a href="https://www.linkedin.com/company/precision-digital-health-inc/about/" target="_blank" rel="noopener noreferrer" class="block">
                    <img 
                      src="/team/Haritha_Yellasiri.jpg" 
                      alt="Haritha Yellasiri" 
                      class="w-32 h-32 rounded-full object-cover border-2 border-slate-600 grayscale group-hover:grayscale-0 transition-all duration-300"
                    />
                    <div class="absolute inset-0 rounded-full bg-blue-600 bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                      <svg class="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </div>
                  </a>
                </div>
                <h4 class="text-lg font-semibold text-gray-900">Haritha Yellasiri</h4>
                <p class="text-sm text-gray-600 font-medium">Director, SQA & Release Management</p>
              </div>
            </div>
          </div>

          <!-- Advisors -->
          <div class="mb-12">
            <h3 class="text-2xl font-bold text-gray-900 mb-8">Advisors</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
              <!-- Garrett Vygantas -->
              <div class="text-center">
                <div class="relative mb-4 group w-32 h-32 mx-auto">
                  <a href="https://www.linkedin.com/company/precision-digital-health-inc/about/" target="_blank" rel="noopener noreferrer" class="block">
                    <img 
                      src="/team/6ca2C3rT.jpg" 
                      alt="Garrett Vygantas" 
                      class="w-32 h-32 rounded-full object-cover mx-auto border-2 border-slate-600 grayscale group-hover:grayscale-0 transition-all duration-300"
                    />
                    <div class="absolute inset-0 w-32 h-32 rounded-full bg-blue-600 bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                      <svg class="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </div>
                  </a>
                </div>
                <h4 class="text-lg font-semibold text-gray-900">Garrett Vygantas, M.D.</h4>
                <p class="text-sm text-gray-600 font-medium">Managing Director</p>
              </div>

              <!-- Aaron Kamauu -->
              <div class="text-center">
                <div class="relative mb-4 group w-32 h-32 mx-auto">
                  <a href="https://www.linkedin.com/company/precision-digital-health-inc/about/" target="_blank" rel="noopener noreferrer" class="block">
                    <img 
                      src="/team/aaron_kamauu.jpg" 
                      alt="Aaron Kamauu" 
                      class="w-32 h-32 rounded-full object-cover mx-auto border-2 border-slate-600 grayscale group-hover:grayscale-0 transition-all duration-300"
                    />
                    <div class="absolute inset-0 w-32 h-32 rounded-full bg-blue-600 bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                      <svg class="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </div>
                  </a>
                </div>
                <h4 class="text-lg font-semibold text-gray-900">Aaron Kamauu, MD MS MPH</h4>
                <p class="text-sm text-gray-600 font-medium">Managing Director</p>
              </div>
            </div>
          </div>

          <!-- Investors and Board of Directors -->
          <div class="mb-8">
            <h3 class="text-2xl font-bold text-gray-900 mb-8">Investors and Board of Directors</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
              <!-- Ren Roome -->
              <div class="text-center">
                <div class="relative mb-4 group w-32 h-32 mx-auto">
                  <a href="https://www.linkedin.com/company/precision-digital-health-inc/about/" target="_blank" rel="noopener noreferrer" class="block">
                    <img 
                      src="/team/Ren-Roome-Analyst-327x380-1.jpg" 
                      alt="Ren Roome" 
                      class="w-32 h-32 rounded-full object-cover mx-auto border-2 border-slate-600 grayscale group-hover:grayscale-0 transition-all duration-300"
                    />
                    <div class="absolute inset-0 w-32 h-32 rounded-full bg-blue-600 bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                      <svg class="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </div>
                  </a>
                </div>
                <h4 class="text-lg font-semibold text-gray-900">Ren Roome</h4>
                <p class="text-sm text-gray-600 font-medium">Board Member (Boston Millennia Partners)</p>
              </div>

              <!-- Dana Callow -->
              <div class="text-center">
                <div class="relative mb-4 group w-32 h-32 mx-auto">
                  <a href="https://www.linkedin.com/company/precision-digital-health-inc/about/" target="_blank" rel="noopener noreferrer" class="block">
                    <img 
                      src="/team/DanaCallow.millennia.partners9-1.jpg" 
                      alt="Dana Callow" 
                      class="w-32 h-32 rounded-full object-cover mx-auto border-2 border-slate-600 grayscale group-hover:grayscale-0 transition-all duration-300"
                    />
                    <div class="absolute inset-0 w-32 h-32 rounded-full bg-blue-600 bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                      <svg class="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </div>
                  </a>
                </div>
                <h4 class="text-lg font-semibold text-gray-900">Dana Callow</h4>
                <p class="text-sm text-gray-600 font-medium">Lead Investor (Boston Millennia Partners)</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
{/if}

<!-- Footer always at the bottom, full-width, no border/frame, no extra padding -->
<section class="w-full text-white py-12 relative" style="background: url('/hero_4.jpg') center/cover no-repeat;">
  <div class="absolute inset-0 bg-[#334155]/40"></div>
  <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="grid md:grid-cols-4 gap-8">
      <div class="col-span-2">
        <img src="/summa-logo-big.png" alt="SUMMA Portal" class="h-8 mb-4" />
        <p class="text-white/90 mb-4 max-w-md">
          Real-time continuous glucose monitoring for clinical research by Precision Digital Health Inc. Transform your trials with instant CGM data capture and live analytics.
        </p>

      </div>
      
      <div>
        <h3 class="text-sm font-semibold text-white tracking-wider uppercase mb-4">Product</h3>
        <ul class="space-y-2">
          <li><a href="/login" class="text-white hover:text-white/80">Sign In</a></li>
        </ul>
      </div>
      
      <div>
        <h3 class="text-sm font-semibold text-white tracking-wider uppercase mb-4">Company</h3>
        <ul class="space-y-2">
          <li><a href="https://precisiondigitalhealth.com" target="_blank" class="text-white hover:text-white/80">Precision Digital Health</a></li>
          <li><a href="mailto:info@precisiondigitalhealth.com" class="text-white hover:text-white/80">Contact Us</a></li>
          <li><a href="tel:949-343-0079" class="text-white hover:text-white/80">(949) 343-0079</a></li>
        </ul>
      </div>
    </div>
  </div>
  <div class="mt-4 pt-4 border-t border-white/20">
    <p class="text-white text-sm text-center">
      © 2025 Precision Digital Health, Inc. All rights reserved.
    </p>
  </div>
</section>

<style>
  .cta-button {
    transition: all 0.3s ease;
  }
  
  .cta-button:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }
</style> 